import { useEffect, useMemo, useState } from "react";
import { Button, Divider, Grid, Modal } from "antd";
import { DownloadOutlined, ExpandOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import type { Texture } from "@textureninja/shared";
import { CDN_URL } from "@/theme.js";
import { categoryMap } from "@/data/category-tree.js";
import { useRecordDownload } from "@/lib/queries.js";
import { downloadTexture } from "./Thumbnail.js";

interface Props {
  texture: Texture;
  open: boolean;
  onClose: () => void;
}

const { useBreakpoint } = Grid;

export function TextureModal({ texture, open, onClose }: Props) {
  const [index, setIndex] = useState(0);
  const recordDownload = useRecordDownload();
  const screens = useBreakpoint();
  const isCompact = !screens.md;

  const images = useMemo(() => {
    if (texture.children?.length) {
      return [
        { id: texture.id, key: texture.key, width: texture.width, height: texture.height },
        ...texture.children,
      ];
    }
    return [{ id: texture.id, key: texture.key, width: texture.width, height: texture.height }];
  }, [texture]);

  const [lightboxOpen, setLightboxOpen] = useState(false);

  const current = images[index]!;
  const tags = texture.tags ?? [];
  const categories = texture.categories ?? [];

  // Database stores thumbnail dimensions, not the original. Probe the
  // original via a JS Image() so we can show the true pixel size.
  const [realDims, setRealDims] = useState<{ width: number; height: number } | null>(null);
  useEffect(() => {
    setRealDims(null);
    const img = new Image();
    img.src = `${CDN_URL}${current.key}`;
    const handleLoad = () => {
      setRealDims({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.addEventListener("load", handleLoad);
    return () => img.removeEventListener("load", handleLoad);
  }, [current.key]);

  const slides = images.map((img, i) => {
    const useReal = i === index && realDims;
    return {
      src: `${CDN_URL}${img.key}`,
      width: useReal ? realDims.width : img.width,
      height: useReal ? realDims.height : img.height,
      alt: img.key,
    };
  });

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width="min(1200px, 95vw)"
        centered
        styles={{ body: { padding: 0 }, content: { padding: 0, overflow: "hidden" } }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "1fr 320px",
            gap: 0,
          }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "radial-gradient(circle at 50% 50%, #1a1c20 0%, #0c0d10 100%)",
              padding: 20,
              minHeight: isCompact ? "40vh" : "70vh",
            }}
          >
            <img
              src={`${CDN_URL}thumb-large-${current.key}`}
              alt={current.key}
              onClick={() => setLightboxOpen(true)}
              style={{
                maxWidth: "100%",
                maxHeight: isCompact ? "50vh" : "75vh",
                cursor: "zoom-in",
                borderRadius: 6,
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              }}
            />
            <button
              type="button"
              aria-label="Open full size"
              onClick={() => setLightboxOpen(true)}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 500,
                color: "#fff",
                background: "rgba(0,0,0,0.55)",
                border: "1px solid rgba(255,255,255,0.15)",
                cursor: "pointer",
                backdropFilter: "blur(6px)",
              }}
            >
              <ExpandOutlined />
              <span>Full size</span>
            </button>
          </div>

          <aside
            style={{
              padding: 24,
              display: "flex",
              flexDirection: "column",
              gap: 16,
              borderLeft: isCompact ? "none" : "1px solid rgba(255,255,255,0.06)",
              borderTop: isCompact ? "1px solid rgba(255,255,255,0.06)" : "none",
              background: "var(--tn-panel)",
            }}
          >
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: "-0.005em",
                  wordBreak: "break-word",
                }}
              >
                {current.key}
              </h3>
              <p
                style={{
                  margin: "4px 0 0",
                  color: "rgba(255,255,255,0.55)",
                  fontSize: 13,
                  minHeight: 18,
                }}
              >
                {realDims
                  ? `${realDims.width.toLocaleString()} \u00d7 ${realDims.height.toLocaleString()} px`
                  : "\u2026"}
              </p>
            </div>

            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="large"
              block
              onClick={() => downloadTexture(current.key, current.id, recordDownload.mutate)}
            >
              Download
            </Button>

            {categories.length > 0 && (
              <div>
                <SectionLabel>{categories.length > 1 ? "Categories" : "Category"}</SectionLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {categories.map((catId) => {
                    const cat = categoryMap.get(catId);
                    if (!cat) return null;
                    const link = cat.children
                      ? `/category/${cat.category}/${cat.id}`
                      : `/textures/${cat.category}/${cat.id}`;
                    const parent = cat.parent_id ? categoryMap.get(cat.parent_id) : null;
                    return (
                      <span key={catId} style={{ fontSize: 13, color: "rgba(255,255,255,0.85)" }}>
                        {parent && (
                          <>
                            <Link to={`/category/${parent.category}/${parent.id}`}>
                              {parent.category}
                            </Link>
                            <span style={{ color: "rgba(255,255,255,0.3)" }}> / </span>
                          </>
                        )}
                        <Link to={link}>{cat.category}</Link>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {tags.length > 0 && (
              <div>
                <SectionLabel>Tags</SectionLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {tags.map((t) => (
                    <Link
                      key={t.tag}
                      to={`/search?tag=${encodeURIComponent(t.tag)}`}
                      onClick={onClose}
                      className="tn-tag"
                    >
                      {t.tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <Divider style={{ margin: "4px 0" }} />
            <p
              style={{
                margin: 0,
                fontSize: 12,
                lineHeight: 1.5,
                color: "rgba(255,255,255,0.45)",
              }}
            >
              Released into the public domain (CC0). Free for personal &amp; commercial use, no
              attribution required.
            </p>
          </aside>
        </div>

        {images.length > 1 && (
          <div
            style={{
              display: "flex",
              gap: 6,
              padding: 12,
              borderTop: "1px solid rgba(255,255,255,0.06)",
              overflowX: "auto",
              background: "var(--tn-chrome)",
            }}
          >
            {images.map((img, i) => (
              <button
                key={img.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Show image ${i + 1}`}
                aria-pressed={i === index}
                style={{
                  flex: "0 0 auto",
                  padding: 0,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  borderRadius: 6,
                  outline:
                    i === index ? "2px solid var(--ant-color-primary)" : "2px solid transparent",
                  outlineOffset: 1,
                  transition: "opacity 150ms ease",
                  opacity: i === index ? 1 : 0.6,
                }}
              >
                <img
                  src={`${CDN_URL}thumb-${img.key}`}
                  alt={img.key}
                  style={{
                    height: 72,
                    width: "auto",
                    borderRadius: 4,
                    display: "block",
                  }}
                />
              </button>
            ))}
          </div>
        )}
      </Modal>
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={slides}
        index={index}
        on={{ view: ({ index: newIndex }) => setIndex(newIndex) }}
        plugins={[Thumbnails]}
      />
    </>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: "rgba(255,255,255,0.5)",
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}
